import { Portal } from '@aviv-estimation-funnel/estimation-data-models/enums';
import { autocompleteAPI, legacyToAvivGeoDataAPI, placeAPI } from '@mfe-services/geoApiRequest';
import type {
    AutocompleteData,
    GeoPlace,
    GermanyAutocompleteRequestPayload,
    LegacyPlaceParams,
    PlaceParams,
    TAddressLevel,
} from '@mfe-types/geo';
import {
    AddressLevel,
    LegacyType,
    PlaceParamsWithAtLeastPostalCode,
    PlaceTypes,
} from '@mfe-types/geo';
import { TLocale } from '@aviv-estimation-funnel/shells/src/app/helpers/i18n';
import { Locale } from '@aviv-unified-frontend/runtime/i18n';

import { AVIV_GEO_RESULT_LIMIT, getAutoCompleteRequestPayload, getPortalCountry } from './common';
import {
    extractAutocompleteData,
    extractDataAtCityLevel,
    extractDataAtHouseNumberLevel,
    extractDataAtPostalCodeLevel,
    extractDataAtStreetLevel,
    filterCityResults,
} from './extractor';

const initialData = {
    postalCode: '',
    cityName: '',
    cityId: '',
    streetName: '',
    streetId: '',
    houseNumber: '',
    addressId: '',
};

export const generateHandoverAutocompletePayload = ({
    postalCode,
    cityName,
    streetName,
    houseNumber,
    isFrenchPortal,
}: PlaceParamsWithAtLeastPostalCode) => {
    let text = postalCode;
    let addressLevel: TAddressLevel = AddressLevel.City;

    if (postalCode && cityName && streetName && houseNumber) {
        text = isFrenchPortal
            ? `${houseNumber} ${streetName}, ${postalCode} ${cityName}`
            : `${streetName} ${houseNumber}, ${postalCode} ${cityName}`;
        addressLevel = AddressLevel.HouseNumber;
    } else if (postalCode && cityName && streetName) {
        text = `${streetName}, ${postalCode} ${cityName}`;
        addressLevel = AddressLevel.Street;
    } else if (postalCode && cityName) {
        text = `${postalCode} ${cityName}`;
    }
    return { text, addressLevel };
};

export const getParamFromURL = (param: string) => {
    return new URLSearchParams(window.location.search)?.get(param);
};

export const getLocationParamsFromURL = (): PlaceParams => {
    const urlParams = new URLSearchParams(window.location.search);
    return {
        postalCode: urlParams.get('zip'),
        cityName: urlParams.get('city_name') ?? urlParams.get('location'),
        streetName: urlParams.get('street'),
        houseNumber: urlParams.get('nr'),
        geoId: urlParams.get('geo_id'),
    };
};

export const getLegacyLocationParamsFromURL = (): LegacyPlaceParams => {
    /* The geo API legacy endpoint allows retrieving locations using legacy id and type.
    Supported types are 'streets', 'addresses', and 'cities'. Both id and type are required.*/
    let id: string | null = null;
    let type: LegacyType | null = null;

    // Mapping for retro-compatibility checks
    const legacyMapping: Record<string, LegacyType> = {
        city: 'cities',
        street: 'streets',
        address: 'addresses',
    };

    const urlParams = new URLSearchParams(window.location.search);

    // Retrieve retro-compatibility for mobile on portal SeLoger
    const mobileId = urlParams.get('addressId');
    const mobileType = urlParams.get('placeType') as LegacyType | null;

    // Retrieve retro-compatibility with other entry points
    const legacyId = urlParams.get('id');
    const legacyType = urlParams.get('type') as keyof typeof legacyMapping | null;

    // Determine Id and type based on retro-compatibility logic
    if (mobileId && mobileType && ['streets', 'addresses', 'cities'].includes(mobileType)) {
        id = mobileId;
        type = mobileType;
    } else if (legacyId && legacyType && legacyMapping[legacyType]) {
        id = legacyId;
        type = legacyMapping[legacyType];
    }

    return { id, type };
};

const extractDataWithPlaceType = (response: GeoPlace, isGermanPortal: boolean, locale: Locale) => {
    const { type_key: placeType } = response;
    switch (placeType) {
        case PlaceTypes.HouseNumber:
            return extractDataAtHouseNumberLevel(response, isGermanPortal, locale);
        case PlaceTypes.Street:
            return extractDataAtStreetLevel(response, isGermanPortal, locale);
        case PlaceTypes.Borough:
        case PlaceTypes.Municipality:
        default:
            return extractDataAtCityLevel([response], locale);
    }
};

const extractDataWithAddressLevel = (
    responses: Array<GeoPlace>,
    addressLevel: TAddressLevel,
    isGermanPortal: boolean,
    locale: Locale,
) => {
    // Case with multiple results is covered only at city level
    const response = responses[0];
    const cityParam = getParamFromURL('city_name') ?? getParamFromURL('location');
    switch (addressLevel) {
        case AddressLevel.HouseNumber:
            return extractDataAtHouseNumberLevel(response, isGermanPortal, locale);
        case AddressLevel.Street:
            return extractDataAtStreetLevel(response, isGermanPortal, locale);
        case AddressLevel.City:
        default:
            if (cityParam) return extractDataAtCityLevel(responses, locale, cityParam);
            else return extractDataAtPostalCodeLevel(response);
    }
};

const extractCitiesForSelector = (
    responses: Array<GeoPlace>,
    addressLevel: TAddressLevel,
    text: string,
    locale: TLocale,
) => {
    if (addressLevel === AddressLevel.City) {
        // we want to fill in the city selector with the city choice(s)
        const filteredItems = filterCityResults(responses, text);
        const extractedCities = extractAutocompleteData(filteredItems, locale);
        return {
            ...(extractedCities?.length && { fetchedCities: extractedCities }),
        };
    }
    return;
};

export const fetchAutocompleteApiForHandover = async ({
    text,
    portal,
    addressLevel,
    limit = AVIV_GEO_RESULT_LIMIT,
    isGermanPortal,
    locale,
}: GermanyAutocompleteRequestPayload): Promise<{
    postalCode: string | undefined;
    cityName?: string;
    cityId?: string;
    streetName?: string;
    streetId?: string;
    houseNumber?: string;
    addressId?: string;
    fetchedCities?: Array<AutocompleteData>;
}> => {
    const params = getAutoCompleteRequestPayload({ text, portal, addressLevel, limit });
    const { items: responses } = await autocompleteAPI(params);

    return {
        ...initialData,
        ...extractDataWithAddressLevel(responses, addressLevel, isGermanPortal, locale),
        ...extractCitiesForSelector(responses, addressLevel, text, locale),
    };
};

export const fetchPlaceApi = async (placeId: string, isGermanPortal: boolean, locale: TLocale) => {
    const { item: response } = await placeAPI(placeId);

    /* At borough or municipality level, we don't use extractCitiesForSelector 
    (cf. fetchAutocompleteAPIForHandover). We are going to call twice the geoapi,
    because we want a fetchAutocompleteAPI to be called after this one.
    fetchAutocompleteAPI is triggered by the postalCode change (cf. useEffect).
    The reason is, for a postal code with several corresponding cities, 
    by calling only placeAPI, we only get one among them.
    For info, this is an edge case, the user normally will never land on 
    the wlef by having previously indicated only the postal code*/

    return {
        ...initialData,
        ...extractDataWithPlaceType(response, isGermanPortal, locale),
    };
};

// TODO VEGA-1209 remove legacy code
export const fetchMappingApiFromLegacyData = async (
    portal: Portal,
    legacyParams: LegacyPlaceParams,
    isGermanPortal: boolean,
    locale: Locale,
) => {
    const platform = getPortalCountry(portal);

    if (platform && legacyParams.id && legacyParams.type) {
        const { item: response } = await legacyToAvivGeoDataAPI(
            platform,
            legacyParams.id,
            legacyParams.type,
        );

        /** Same comment as for fetchPlaceApi is applicable here */

        return {
            ...initialData,
            ...extractDataWithPlaceType(response, isGermanPortal, locale),
        };
    }

    return;
};
